import ContentPasteIcon from '@mui/icons-material/ContentPaste';
import GroupOutlinedIcon from '@mui/icons-material/GroupOutlined';
import LinkIcon from '@mui/icons-material/Link';
import LiveTvOutlinedIcon from '@mui/icons-material/LiveTvOutlined';
import { alpha, Box, Card, MenuItem, Skeleton, Stack, TextField, Theme, Typography, useTheme } from '@mui/material';
import moment from 'moment';
import React, { useMemo } from 'react';
import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import useOnlyPromoteStats, { OnlyPromoteStatsBucket } from '../../../hooks/useOnlyPromoteStats';

const SERIES_LABELS = {
    linkClicks: 'Link Clicks',
    referrerClicks: 'Referrer Clicks',
    menuCopies: 'Menu Copies',
    liveStreamClicks: 'Live Stream Clicks',
};

const DAYS_OPTIONS = [7, 14, 30, 60, 90];

const sumBucket = (buckets: OnlyPromoteStatsBucket[] | undefined): number => (buckets ?? []).reduce((acc, b) => acc + b.count, 0);

interface StatRowCardProps {
    title: string;
    data: OnlyPromoteStatsBucket[] | undefined;
    isLoading: boolean;
    icon: React.ReactNode;
    iconBg: string;
    iconColor: string;
    countColor: string;
    barFill: string;
    barStroke: string;
}

const StatRowCard: React.FC<StatRowCardProps> = ({ title, data, isLoading, icon, iconBg, iconColor, countColor, barFill, barStroke }) => {
    const total = sumBucket(data);
    const chartData = useMemo(() => (data ?? []).map(b => ({ ...b, label: moment(b.date).format('MMM D') })), [data]);

    return (
        <Box>
            <Card
                variant="outlined"
                sx={{
                    py: 1.5,
                    px: { xs: 1.5, sm: 2 },
                }}
            >
                <Stack direction="column" spacing={0.25}>
                    <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500, textTransform: 'none' }}>
                        {title}
                    </Typography>

                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: { xs: 1.5, sm: 2 },
                        }}
                    >
                        <Box
                            sx={{
                                width: { xs: 24, sm: 24 },
                                height: { xs: 24, sm: 24 },
                                borderRadius: '50%',
                                bgcolor: iconBg,
                                color: iconColor,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                flexShrink: 0,
                            }}
                        >
                            {icon}
                        </Box>
                        <Stack spacing={0.25} sx={{ minWidth: { xs: 54, sm: 78 }, flexShrink: 0 }}>
                            {isLoading ? (
                                <Skeleton variant="text" width={40} height={40} />
                            ) : (
                                <Typography variant="h6" sx={{ fontWeight: 700, color: countColor, lineHeight: 1.1 }}>
                                    {total.toLocaleString()}
                                </Typography>
                            )}
                        </Stack>
                        <Box sx={{ flex: 1, minWidth: 0, height: { xs: 72, sm: 88 }, ml: { xs: 0, sm: 0.5 } }}>
                            {isLoading ? (
                                <Skeleton variant="rectangular" width="100%" height="100%" sx={{ borderRadius: 1 }} />
                            ) : (
                                <ResponsiveContainer width="100%" height="100%">
                                    <BarChart data={chartData} margin={{ top: 4, right: 2, bottom: 0, left: -18 }}>
                                        <CartesianGrid strokeDasharray="3 3" vertical={false} opacity={0.35} />
                                        <XAxis
                                            dataKey="label"
                                            tick={{ fontSize: 9 }}
                                            interval="preserveStartEnd"
                                            minTickGap={8}
                                            axisLine={false}
                                            tickLine={false}
                                        />
                                        <YAxis tick={{ fontSize: 9 }} allowDecimals={false} axisLine={false} tickLine={false} width={22} />
                                        <Tooltip
                                            cursor={{ fill: 'rgba(0,0,0,0.04)' }}
                                            labelFormatter={label => moment(label as string, 'MMM D').format('ddd, MMM D')}
                                            formatter={value => [Number(value).toLocaleString(), title]}
                                        />
                                        <Bar dataKey="count" fill={barFill} stroke={barStroke} radius={[2, 2, 0, 0]} maxBarSize={14} />
                                    </BarChart>
                                </ResponsiveContainer>
                            )}
                        </Box>
                    </Box>
                </Stack>
            </Card>
        </Box>
    );
};

interface OnlyPromoteStatsCardProps {
    days: number;
    onDaysChange: (days: number) => void;
}

const OnlyPromoteStatsCard: React.FC<OnlyPromoteStatsCardProps> = ({ days, onDaysChange }) => {
    const { data, isLoading, isFetching } = useOnlyPromoteStats({ days });
    const theme: Theme = useTheme();

    return (
        <Box>
            <Stack spacing={2}>
                <Stack direction={{ xs: 'column', sm: 'row' }} alignItems={{ sm: 'center' }} justifyContent="space-between" spacing={1.5}>
                    <Typography variant="body2" color="text.secondary">
                        Daily traffic on your public profile, synced from onlypromote.me.
                        {isFetching && !isLoading ? ' Refreshing…' : ''}
                    </Typography>
                    <TextField
                        select
                        size="small"
                        label="Window"
                        value={days}
                        onChange={e => onDaysChange(Number.parseInt(e.target.value, 10))}
                        sx={{ minWidth: 160, flexShrink: 0 }}
                    >
                        {DAYS_OPTIONS.map(d => (
                            <MenuItem key={d} value={d}>
                                Last {d} days
                            </MenuItem>
                        ))}
                    </TextField>
                </Stack>
                <Stack spacing={1.5}>
                    <StatRowCard
                        title={SERIES_LABELS.linkClicks}
                        data={data?.linkClicks}
                        isLoading={isLoading}
                        icon={<LinkIcon sx={{ fontSize: 16 }} />}
                        iconBg={alpha('#1976d2', 0.12)}
                        iconColor="#1976d2"
                        countColor={alpha(theme.palette.primary.main, 0.85)}
                        barFill={alpha(theme.palette.primary.main, 0.85)}
                        barStroke={alpha(theme.palette.primary.main, 0.85)}
                    />
                    <StatRowCard
                        title={SERIES_LABELS.referrerClicks}
                        data={data?.referrerClicks}
                        isLoading={isLoading}
                        icon={<GroupOutlinedIcon sx={{ fontSize: 16 }} />}
                        iconBg={alpha('#ed6c02', 0.14)}
                        iconColor="#ed6c02"
                        countColor={alpha(theme.palette.secondary.main, 0.85)}
                        barFill={alpha(theme.palette.secondary.main, 0.85)}
                        barStroke={alpha(theme.palette.secondary.main, 0.85)}
                    />
                    <StatRowCard
                        title={SERIES_LABELS.menuCopies}
                        data={data?.menuCopies}
                        isLoading={isLoading}
                        icon={<ContentPasteIcon sx={{ fontSize: 16 }} />}
                        iconBg={alpha('#2e7d32', 0.12)}
                        iconColor="#2e7d32"
                        countColor={alpha(theme.palette.success.main, 0.85)}
                        barFill={alpha(theme.palette.success.main, 0.85)}
                        barStroke={alpha(theme.palette.success.main, 0.85)}
                    />
                    <StatRowCard
                        title={SERIES_LABELS.liveStreamClicks}
                        data={data?.liveStreamClicks}
                        isLoading={isLoading}
                        icon={<LiveTvOutlinedIcon sx={{ fontSize: 16 }} />}
                        iconBg={alpha('#9c27b0', 0.12)}
                        iconColor="#9c27b0"
                        countColor={alpha('#9c27b0', 0.92)}
                        barFill={alpha('#9c27b0', 0.85)}
                        barStroke={alpha('#9c27b0', 0.85)}
                    />
                </Stack>
            </Stack>
        </Box>
    );
};

export default OnlyPromoteStatsCard;
